import { produce } from "immer";
import { GlobalActions, resetAction } from "../../globals/store/actions";
import { createEntityAdapter } from "../../helpers/store/create-entity-adapter";
import { EntityState } from "../../helpers/store/entity-state.interface";
import { Conversation, Message } from "../interfaces/conversations.interface";
import { pickBetterAvatar } from "../utils/avatar-url";
import {
  dedupeSocialMessages,
  isDuplicateSocialMessage,
} from "../utils/dedupe-messages";
import { ConversationStatus } from "../types/conversation-status.types";
import {
  addInstagramMessageAction,
  removeConversationFromStackByConversationId,
  SocialChatAction,
  setSelectedInstagramConversationAction,
  setSelectedFacebookConversationAction,
  addFacebookMessageAction,
  setSelectedLinkedinConversationAction,
  addLinkedinMessageAction,
  setSelectedLinkedinBusinessConversationAction,
  addLinkedinBusinessMessageAction,
  setSelectedWhatsappConversationAction,
  addWhatsappMessageAction,
  addWhatsappConversationAction,
  setConversationSocialAction,
  setDateFilters,
  updateConversationOwners,
  updateConversationParticipantState,
} from "./actions";
import {
  SocialChatApiAction,
  getInstagramConversationsFailureAction,
  getInstagramConversationsInitAction,
  getInstagramConversationsSuccessAction,
  getInstagramMessagesFailureAction,
  getInstagramMessagesInitAction,
  getInstagramMessagesSuccessAction,
  postInstagramMessageFailureAction,
  postInstagramMessageInitAction,
  postInstagramMessageSuccessAction,
  postFacebookMessageSuccessAction,
  getFacebookConversationsSuccessAction,
  getFacebookMessagesSuccessAction,
  getFacebookConversationsInitAction,
  getFacebookConversationsFailureAction,
  getFacebookMessagesFailureAction,
  postFacebookMessageInitAction,
  postFacebookMessageFailureAction,
  postFacebookSeenMessageInitAction,
  postFacebookSeenMessageSuccessAction,
  postFacebookSeenMessageFailureAction,
  getWhatsappConversationsSuccessAction,
  getWhatsappMessagesSuccessAction,
  postWhatsappMessageSuccessAction,
  selectConversationStatusToShow,
  getLinkedinConversationsSuccessAction,
  getLinkedinConversationsInitAction,
  getLinkedinConversationsFailureAction,
  getLinkedinMessagesSuccessAction,
  getLinkedinMessagesInitAction,
  getLinkedinMessagesFailureAction,
  postLinkedinMessageSuccessAction,
  postLinkedinMessageInitAction,
  postLinkedinMessageFailureAction,
  getLinkedinBusinessConversationsSuccessAction,
  getLinkedinBusinessConversationsInitAction,
  getLinkedinBusinessConversationsFailureAction,
  getLinkedinBusinessMessagesSuccessAction,
  getLinkedinBusinessMessagesInitAction,
  getLinkedinBusinessMessagesFailureAction,
  postLinkedinBusinessMessageSuccessAction,
  postLinkedinBusinessMessageInitAction,
  postLinkedinBusinessMessageFailureAction,
} from "./api-actions";
import { SocialType } from "../types/social.types";
import { syncFacebookConversationWithContactAction as syncMetaConversationWithContactAction } from "./actions";
import { DateFilter } from "../interfaces/date-filter.interface";
export interface SocialChatState {
  selectedSocial: SocialType;
  suppressNotificationSoundUntil: number;
  dateFilter: DateFilter;
  instagram: {
    active: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    inTrash: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    archived: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  conversationStatus: ConversationStatus;
  facebook: {
    active: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    inTrash: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    archived: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  linkedin: {
    active: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    inTrash: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    archived: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  linkedinBusiness: {
    active: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    inTrash: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    archived: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
  whatsapp: {
    active: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    inTrash: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    archived: {
      ids: number[];
      entities: Record<string, Conversation>;
    };
    filteredConversations: {
      active: Conversation[];
      archived: Conversation[];
      inTrash: Conversation[];
    };
    selected: Conversation | null;
    messages: {
      ids: number[];
      entities: Record<string, Message>;
    };
  };
}

const initialState: SocialChatState = {
  conversationStatus: "active",
  selectedSocial: "all",
  suppressNotificationSoundUntil: 0,
  dateFilter: {
    from: "",
    to: "",
  },
  instagram: {
    active: {
      ids: [],
      entities: {},
    },
    archived: {
      ids: [],
      entities: {},
    },
    inTrash: {
      ids: [],
      entities: {},
    },

    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  facebook: {
    active: {
      ids: [],
      entities: {},
    },
    archived: {
      ids: [],
      entities: {},
    },
    inTrash: {
      ids: [],
      entities: {},
    },

    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  linkedin: {
    active: {
      ids: [],
      entities: {},
    },
    archived: {
      ids: [],
      entities: {},
    },
    inTrash: {
      ids: [],
      entities: {},
    },

    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  linkedinBusiness: {
    active: {
      ids: [],
      entities: {},
    },
    archived: {
      ids: [],
      entities: {},
    },
    inTrash: {
      ids: [],
      entities: {},
    },

    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
  whatsapp: {
    active: {
      ids: [],
      entities: {},
    },
    archived: {
      ids: [],
      entities: {},
    },
    inTrash: {
      ids: [],
      entities: {},
    },
    filteredConversations: {
      active: [],
      archived: [],
      inTrash: [],
    },
    selected: null,
    messages: {
      ids: [],
      entities: {},
    },
  },
};

const conversationsAdapter = createEntityAdapter<Conversation>(
  (payload) => payload.id
);
const messagesAdapter = createEntityAdapter<Message>((payload) => payload.id);

const getInstagramParticipantId = (conversation?: Conversation | null) =>
  conversation?.participants?.[0]?.id || conversation?.fromId || conversation?.id;

const getInstagramParticipantKeys = (conversation?: Conversation | null) => {
  const participant = conversation?.participants?.[0];
  const keys = new Set<string>();
  if (!participant && !conversation) return keys;

  for (const value of [
    conversation?.id,
    conversation?.fromId,
    participant?.id,
    ...(participant?.socials || []).map((social) => social.socialUniqueId),
  ]) {
    const normalized = String(value || "").trim();
    if (normalized) keys.add(normalized);
  }
  return keys;
};

const getFacebookParticipantKeys = (conversation?: Conversation | null) => {
  const participant = conversation?.participants?.[0];
  const keys = new Set<string>();
  if (!participant && !conversation) return keys;

  for (const value of [
    conversation?.id,
    conversation?.fromId,
    participant?.id,
    ...(participant?.socials || []).map((social) => social.socialUniqueId),
  ]) {
    const normalized = String(value || "").trim();
    if (normalized) keys.add(normalized);
  }
  return keys;
};

const mergeParticipantAvatar = (
  participants: Conversation["participants"],
  from?: { id?: string; avatarUrl?: string }
) => {
  const avatarUrl = from?.avatarUrl?.trim();
  if (!avatarUrl || !from?.id) return participants;
  return participants.map((participant) => {
    if (participant.id !== from.id) return participant;
    const resolvedAvatar = pickBetterAvatar(participant.avatarUrl, avatarUrl);
    if (!resolvedAvatar || resolvedAvatar === participant.avatarUrl) {
      return participant;
    }
    return { ...participant, avatarUrl: resolvedAvatar };
  });
};

const enrichInboundMessageAvatar = (
  message: Message,
  participants: Conversation["participants"]
): Message => {
  const participant = participants.find((p) => p.id === message.from.id);
  const avatarUrl = pickBetterAvatar(
    participant?.avatarUrl,
    message.from.avatarUrl
  );
  if (!avatarUrl || avatarUrl === message.from.avatarUrl?.trim()) return message;
  return {
    ...message,
    from: { ...message.from, avatarUrl },
  };
};

const getConversationParticipantId = (conversation?: Conversation | null) =>
  conversation?.participants?.[0]?.id || conversation?.fromId || conversation?.id;

const collectParticipantAvatars = (...stacks: EntityState<Conversation>[]) => {
  const avatarByParticipantId = new Map<string, string>();
  for (const stack of stacks) {
    for (const id of stack.ids) {
      const conversation = stack.entities[id];
      const participantId = getConversationParticipantId(conversation);
      const avatarUrl = conversation?.participants?.[0]?.avatarUrl?.trim();
      if (participantId && avatarUrl) {
        avatarByParticipantId.set(String(participantId), avatarUrl);
      }
    }
  }
  return avatarByParticipantId;
};

const preserveConversationAvatars = (
  incoming: Conversation[],
  avatarByParticipantId: Map<string, string>
) =>
  incoming.map((conversation) => {
    const incomingAvatar = conversation.participants[0]?.avatarUrl?.trim();
    if (incomingAvatar) return conversation;
    const participantId = getConversationParticipantId(conversation);
    const preservedAvatar = participantId
      ? avatarByParticipantId.get(String(participantId))
      : undefined;
    if (!preservedAvatar) return conversation;
    return {
      ...conversation,
      participants: conversation.participants.map((participant, index) =>
        index === 0 ? { ...participant, avatarUrl: preservedAvatar } : participant
      ),
    };
  });

const findContactAvatarInMessages = (
  messages: Message[],
  participantId?: string
) => {
  if (!participantId) return undefined;
  for (const message of messages) {
    const avatarUrl = message.from.avatarUrl?.trim();
    if (avatarUrl && String(message.from.id) === String(participantId)) {
      return avatarUrl;
    }
  }
  return undefined;
};

const syncSelectedConversation = (
  draft: SocialChatState,
  social: Exclude<SocialType, "all" | "web">
) => {
  const selectedId = draft[social].selected?.id;
  if (!selectedId) return;
  const updated =
    draft[social][draft.conversationStatus].entities[selectedId];
  if (updated) {
    draft[social].selected = updated;
  }
};

const findInstagramConversationByParticipant = (
  stack: EntityState<Conversation>,
  participantId?: string
): Conversation | undefined => {
  if (!participantId) return undefined;
  const target = String(participantId);
  const existingId = stack.ids.find((id) => {
    const conversation = stack.entities[id];
    return getInstagramParticipantKeys(conversation).has(target);
  });
  return existingId ? stack.entities[existingId] : undefined;
};

const findInstagramConversationAcrossStacks = (
  instagram: SocialChatState["instagram"],
  participantId?: string
) => {
  for (const status of ["active", "archived", "inTrash"] as ConversationStatus[]) {
    const conversation = findInstagramConversationByParticipant(
      instagram[status],
      participantId
    );
    if (conversation) return { conversation, status };
  }
  return null;
};

const findFacebookConversationByParticipant = (
  stack: EntityState<Conversation>,
  participantId?: string
): Conversation | undefined => {
  if (!participantId) return undefined;
  const target = String(participantId);
  const existingId = stack.ids.find((id) => {
    const conversation = stack.entities[id];
    return getFacebookParticipantKeys(conversation).has(target);
  });
  return existingId ? stack.entities[existingId] : undefined;
};

const findFacebookConversationAcrossStacks = (
  facebook: SocialChatState["facebook"],
  participantId?: string
) => {
  for (const status of ["active", "archived", "inTrash"] as ConversationStatus[]) {
    const conversation = findFacebookConversationByParticipant(
      facebook[status],
      participantId
    );
    if (conversation) return { conversation, status };
  }
  return null;
};

const removeDuplicateInstagramConversations = (
  incoming: Conversation[],
  stack: EntityState<Conversation>
) => {
  for (const conversation of incoming) {
    const incomingKeys = getInstagramParticipantKeys(conversation);
    if (!incomingKeys.size) continue;

    for (const existingId of [...stack.ids]) {
      if (String(existingId) === String(conversation.id)) continue;
      const existing = stack.entities[existingId];
      const existingKeys = getInstagramParticipantKeys(existing);
      const overlaps = Array.from(incomingKeys).some((key) =>
        existingKeys.has(key)
      );
      if (overlaps) {
        conversationsAdapter.removeOne(existingId, stack);
      }
    }
  }
};

const sortSocialMessages = (messages: Message[]) =>
  dedupeSocialMessages(
    [...messages].sort(
      (a, b) =>
        new Date(a.timestamp.full).getTime() -
          new Date(b.timestamp.full).getTime() ||
        String(a.id).localeCompare(String(b.id))
    )
  );

const listStoredMessages = (messages: EntityState<Message>) =>
  messages.ids.map((id) => messages.entities[id]).filter(Boolean);

const removeMatchingOptimisticMessages = (
  incoming: Message,
  messages: EntityState<Message>
) => {
  const incomingText = String(incoming.message ?? "").trim();
  const incomingFrom = String(incoming.from?.id ?? "");
  for (const id of [...messages.ids]) {
    if (!String(id).startsWith("optimistic-")) continue;
    const stored = messages.entities[id];
    if (!stored) continue;
    if (
      String(stored.from?.id ?? "") === incomingFrom &&
      String(stored.message ?? "").trim() === incomingText
    ) {
      messagesAdapter.removeOne(id, messages);
    }
  }
};

const upsertSocialMessage = (
  incoming: Message,
  messages: EntityState<Message>
) => {
  const stored = listStoredMessages(messages);
  if (isDuplicateSocialMessage(stored, incoming)) return;
  removeMatchingOptimisticMessages(incoming, messages);
  messagesAdapter.setOne(incoming, messages);
};

const removeOptimisticMessages = (messages: EntityState<Message>) => {
  for (const id of [...messages.ids]) {
    if (String(id).startsWith("optimistic-")) {
      messagesAdapter.removeOne(id, messages);
    }
  }
};

const applyOptimisticOutgoingMessage = (
  draft: SocialChatState,
  social: "instagram" | "facebook",
  conversationId: string,
  message: Message
) => {
  draft.suppressNotificationSoundUntil = Date.now() + 5000;
  const stack = draft[social][draft.conversationStatus];
  const conversation = stack.entities[conversationId];
  if (!conversation) return;

  if (draft[social].selected?.id === conversationId) {
    upsertSocialMessage(message, draft[social].messages);
  }

  conversationsAdapter.updateOne(
    {
      ...conversation,
      snippet: message.message,
      messageType: message.type,
      timestamp: message.timestamp,
    },
    stack
  );
  conversationsAdapter.moveToFirstPosition(conversationId, stack);
};

/** Estado guardado en localStorage puede no incluir slices nuevos → evita pantalla en blanco */
function mergePersistedSocialChat(state: SocialChatState): SocialChatState {
  if (!state.linkedin) {
    state = { ...state, linkedin: initialState.linkedin };
  }
  if (!state.linkedinBusiness) {
    state = { ...state, linkedinBusiness: initialState.linkedinBusiness };
  }
  return state;
}

const socialChatImmerReducer = produce(
  (
    draft: SocialChatState = initialState,
    action: SocialChatAction | SocialChatApiAction | GlobalActions
  ) => {
    switch (action.type) {
      case setSelectedInstagramConversationAction.type:
        if (draft.instagram.selected?.id === action.id || !action.id) {
          draft.instagram.selected = null;
        } else {
          const stack = draft.instagram[draft.conversationStatus];
          const conv1 = stack.entities[action.id];
          const avatarByParticipantId = collectParticipantAvatars(
            draft.instagram.active,
            draft.instagram.archived,
            draft.instagram.inTrash
          );
          const [preserved] = preserveConversationAvatars(
            [conv1],
            avatarByParticipantId
          );
          draft.instagram.selected = preserved;

          conversationsAdapter.updateOne(
            { ...preserved, unreadCount: 0 },
            stack
          );
        }
        return draft;
      case updateConversationOwners.type:
        if (action.socialType !== "all" && action.socialType !== "web") {
          const conversation =
            draft[action.socialType][action.statusToUpdate].entities[
              action.conversationId
            ];

          const participant = conversation.participants.find(
            (participant) => participant.id === action.participantId
          );
          const restParticipants = conversation.participants.filter(
            (participant) => participant.id != action.participantId
          );
          conversationsAdapter.updateOne(
            {
              ...conversation,
              participants: [
                ...restParticipants,
                {
                  ...participant,
                  owners: action.newOwners,
                  id: action.participantId,
                  name: `${participant?.name}`,
                },
              ],
            },
            draft[action.socialType][draft.conversationStatus]
          );
          conversationsAdapter.moveToFirstPosition(
            conversation.id,
            draft[action.socialType][draft.conversationStatus]
          );
        }

        return draft;
      case updateConversationParticipantState.type:
        if (action.socialType !== "all" && action.socialType !== "web") {
          const conversation =
            draft[action.socialType][action.statusToUpdate].entities[
              action.conversationId
            ];
          if (!conversation) return draft;

          const participants = conversation.participants.map((participant) =>
            String(participant.id) === String(action.participantId)
              ? { ...participant, state: String(action.state) }
              : participant
          );

          conversationsAdapter.updateOne(
            { ...conversation, participants },
            draft[action.socialType][draft.conversationStatus]
          );
        }
        return draft;
      case setDateFilters.type:
        draft.dateFilter[action.dateToUpdate] = action.date;

        return draft;

      case addInstagramMessageAction.type: {
        let stack = draft.instagram[draft.conversationStatus];
        let conv2: Conversation | undefined = stack.entities[action.id];

        if (!conv2) {
          conv2 = findInstagramConversationByParticipant(
            stack,
            action.message?.from?.id
          );
        }

        if (!conv2) {
          const located = findInstagramConversationAcrossStacks(
            draft.instagram,
            action.message?.from?.id
          );
          if (located) {
            conv2 = located.conversation;
            stack = draft.instagram[located.status];
          }
        }

        if (!conv2) {
          const inbound = action.message;
          conv2 = {
            id: action.id,
            snippet: inbound.message,
            messageType: inbound.type,
            unreadCount: 1,
            timestamp: inbound.timestamp,
            participants: [
              {
                id: inbound.from.id,
                name: inbound.from.name,
                avatarUrl: inbound.from.avatarUrl || "",
              },
            ],
            type: "instagram",
            count: 0,
            fromId: inbound.from.id,
          };
          conversationsAdapter.setOne(conv2, stack);
          if (draft.instagram.selected?.id === conv2.id) {
            upsertSocialMessage(inbound, draft.instagram.messages);
          }
          return draft;
        }

        const participants = mergeParticipantAvatar(
          conv2.participants,
          action.message.from
        );
        const inboundMessage = enrichInboundMessageAvatar(
          action.message,
          participants
        );

        if (draft.instagram.selected?.id === conv2.id) {
          upsertSocialMessage(inboundMessage, draft.instagram.messages);
        } else {
          conv2 = { ...conv2, unreadCount: conv2.unreadCount + 1 };
        }

        conversationsAdapter.updateOne(
          {
            ...conv2,
            participants,
            snippet: inboundMessage.message,
            messageType: inboundMessage.type,
            timestamp: inboundMessage.timestamp,
          },
          stack
        );
        conversationsAdapter.moveToFirstPosition(conv2.id, stack);
        removeDuplicateInstagramConversations([conv2], stack);

        return draft;
      }
      case setConversationSocialAction.type:
        draft.selectedSocial = action.social;
        return draft;
      case postInstagramMessageSuccessAction.type:
        let conv3 =
          draft.instagram[draft.conversationStatus].entities[action.id];
        if (conv3) {
          if (draft.instagram.selected?.id === conv3.id) {
            removeOptimisticMessages(draft.instagram.messages);
            upsertSocialMessage(action.message, draft.instagram.messages);
          } else {
            conv3 = { ...conv3, unreadCount: conv3.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv3,
              snippet: action.message.message,
              messageType: action.message.type,
              timestamp: action.message.timestamp,
            },
            draft.instagram[draft.conversationStatus]
          );
          conversationsAdapter.moveToFirstPosition(
            conv3.id,
            draft.instagram[draft.conversationStatus]
          );
        }

        return draft;
      case getInstagramConversationsSuccessAction.type: {
        const avatarByParticipantId = collectParticipantAvatars(
          draft.instagram.active,
          draft.instagram.archived,
          draft.instagram.inTrash
        );
        const active = preserveConversationAvatars(
          action.active,
          avatarByParticipantId
        );
        const archived = preserveConversationAvatars(
          action.archived,
          avatarByParticipantId
        );
        const inTrash = preserveConversationAvatars(
          action.inTrash,
          avatarByParticipantId
        );
        removeDuplicateInstagramConversations(active, draft.instagram.active);
        removeDuplicateInstagramConversations(inTrash, draft.instagram.inTrash);
        removeDuplicateInstagramConversations(
          archived,
          draft.instagram.archived
        );
        conversationsAdapter.addMany(active, draft.instagram.active);
        conversationsAdapter.addMany(inTrash, draft.instagram.inTrash);
        conversationsAdapter.addMany(archived, draft.instagram.archived);
        syncSelectedConversation(draft, "instagram");
        return draft;
      }
      case removeConversationFromStackByConversationId.type:
        if (action.socialType !== "all" && action.socialType !== "web") {
          const conv1 =
            draft[action.socialType][action.statusToUpdate].entities[
              action.conversationId
            ];

          conversationsAdapter.removeOne(
            action.conversationId,
            draft[action.socialType][action.statusToUpdate]
          );
          conversationsAdapter.setOne(
            conv1,
            draft[action.socialType][action.newStatus]
          );
        }

        return draft;
      case getInstagramMessagesSuccessAction.type: {
        const sortedMessages = sortSocialMessages(action.messages);
        const stack = draft.instagram[draft.conversationStatus];
        const selectedId = draft.instagram.selected?.id;
        if (!selectedId) {
          messagesAdapter.addMany(sortedMessages, draft.instagram.messages);
          return draft;
        }

        const conversation = stack.entities[selectedId];
        if (!conversation) {
          messagesAdapter.addMany(sortedMessages, draft.instagram.messages);
          return draft;
        }

        const participantId = getInstagramParticipantId(conversation);
        const messageAvatarUrl = findContactAvatarInMessages(
          sortedMessages,
          participantId
        );
        const avatarUrl = pickBetterAvatar(
          conversation.participants[0]?.avatarUrl,
          messageAvatarUrl
        );

        const participants = avatarUrl
          ? mergeParticipantAvatar(conversation.participants, {
              id: participantId,
              avatarUrl,
            })
          : conversation.participants;

        const enrichedMessages = sortedMessages.map((message) =>
          enrichInboundMessageAvatar(message, participants)
        );
        messagesAdapter.addMany(enrichedMessages, draft.instagram.messages);

        if (!avatarUrl) return draft;

        const updatedConversation = { ...conversation, participants };
        conversationsAdapter.updateOne(updatedConversation, stack);
        if (draft.instagram.selected?.id === selectedId) {
          draft.instagram.selected = updatedConversation;
        }
        return draft;
      }
      case getInstagramMessagesInitAction.type:
        messagesAdapter.removeAll(draft.instagram.messages);
        return draft;
      case setSelectedFacebookConversationAction.type:
        if (draft.facebook.selected?.fromId === action.id || !action.id) {
          draft.facebook.selected = null;
        } else {
          const stack = draft.facebook[draft.conversationStatus];
          const conv4 = stack.entities[action.id];
          const avatarByParticipantId = collectParticipantAvatars(
            draft.facebook.active,
            draft.facebook.archived,
            draft.facebook.inTrash
          );
          const [preserved] = preserveConversationAvatars(
            [conv4],
            avatarByParticipantId
          );
          draft.facebook.selected = preserved;

          conversationsAdapter.updateOne(
            { ...preserved, unreadCount: 0 },
            stack
          );
        }
        return draft;

      case addFacebookMessageAction.type: {
        let stack = draft.facebook[draft.conversationStatus];
        let conv5: Conversation | undefined = stack.entities[action.id];

        if (!conv5) {
          conv5 = findFacebookConversationByParticipant(
            stack,
            action.message?.from?.id
          );
        }

        if (!conv5) {
          const located = findFacebookConversationAcrossStacks(
            draft.facebook,
            action.message?.from?.id
          );
          if (located) {
            conv5 = located.conversation;
            stack = draft.facebook[located.status];
          }
        }

        if (!conv5) {
          const inbound = action.message;
          conv5 = {
            id: action.id,
            snippet: inbound.message,
            messageType: inbound.type,
            unreadCount: 1,
            timestamp: inbound.timestamp,
            participants: [
              {
                id: inbound.from.id,
                name: inbound.from.name,
                avatarUrl: inbound.from.avatarUrl || "",
              },
            ],
            type: "facebook",
            count: 0,
            fromId: inbound.from.id,
          };
          conversationsAdapter.setOne(conv5, stack);
          if (draft.facebook.selected?.id === conv5.id) {
            upsertSocialMessage(inbound, draft.facebook.messages);
          }
          return draft;
        }

        const participants = mergeParticipantAvatar(
          conv5.participants,
          action.message.from
        );
        const inboundMessage = enrichInboundMessageAvatar(
          action.message,
          participants
        );

        if (draft.facebook.selected?.id === conv5.id) {
          upsertSocialMessage(inboundMessage, draft.facebook.messages);
        } else {
          conv5 = { ...conv5, unreadCount: conv5.unreadCount + 1 };
        }

        conversationsAdapter.updateOne(
          {
            ...conv5,
            participants,
            snippet: inboundMessage.message,
            messageType: inboundMessage.type,
            timestamp: inboundMessage.timestamp,
          },
          stack
        );
        conversationsAdapter.moveToFirstPosition(conv5.id, stack);

        return draft;
      }
      case postFacebookMessageSuccessAction.type:
        let conv6 =
          draft.facebook[draft.conversationStatus].entities[action.id];
        if (conv6) {
          if (draft.facebook.selected?.id === conv6.id) {
            removeOptimisticMessages(draft.facebook.messages);
            upsertSocialMessage(action.message, draft.facebook.messages);
          } else {
            conv6 = { ...conv6, unreadCount: conv6.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv6,
              snippet: action.message.message,
              messageType: action.message.type,
              timestamp: action.message.timestamp,
            },
            draft.facebook[draft.conversationStatus]
          );
          conversationsAdapter.moveToFirstPosition(
            conv6.id,
            draft.facebook[draft.conversationStatus]
          );
        }

        return draft;
      case getFacebookConversationsSuccessAction.type: {
        const avatarByParticipantId = collectParticipantAvatars(
          draft.facebook.active,
          draft.facebook.archived,
          draft.facebook.inTrash
        );
        const active = preserveConversationAvatars(
          action.active,
          avatarByParticipantId
        );
        const archived = preserveConversationAvatars(
          action.archived,
          avatarByParticipantId
        );
        const inTrash = preserveConversationAvatars(
          action.inTrash,
          avatarByParticipantId
        );
        conversationsAdapter.addMany(active, draft.facebook.active);
        conversationsAdapter.addMany(archived, draft.facebook.archived);
        conversationsAdapter.addMany(inTrash, draft.facebook.inTrash);
        syncSelectedConversation(draft, "facebook");
        return draft;
      }
      case getFacebookMessagesSuccessAction.type: {
        const sortedMessages = sortSocialMessages(action.messages);
        const stack = draft.facebook[draft.conversationStatus];
        const selectedId = draft.facebook.selected?.id;
        if (!selectedId) {
          messagesAdapter.addMany(sortedMessages, draft.facebook.messages);
          return draft;
        }

        const conversation = stack.entities[selectedId];
        if (!conversation) {
          messagesAdapter.addMany(sortedMessages, draft.facebook.messages);
          return draft;
        }

        const participantId = getConversationParticipantId(conversation);
        const messageAvatarUrl = findContactAvatarInMessages(
          sortedMessages,
          participantId
        );
        const avatarUrl = pickBetterAvatar(
          conversation.participants[0]?.avatarUrl,
          messageAvatarUrl
        );

        const participants = avatarUrl
          ? mergeParticipantAvatar(conversation.participants, {
              id: participantId,
              avatarUrl,
            })
          : conversation.participants;

        const enrichedMessages = sortedMessages.map((message) =>
          enrichInboundMessageAvatar(message, participants)
        );
        messagesAdapter.addMany(enrichedMessages, draft.facebook.messages);

        if (!avatarUrl) return draft;

        const updatedConversation = { ...conversation, participants };
        conversationsAdapter.updateOne(updatedConversation, stack);
        if (draft.facebook.selected?.id === selectedId) {
          draft.facebook.selected = updatedConversation;
        }
        return draft;
      }
      case setSelectedLinkedinConversationAction.type:
        if (draft.linkedin.selected?.fromId === action.id || !action.id) {
          draft.linkedin.selected = null;
        } else {
          const convLi =
            draft.linkedin[draft.conversationStatus].entities[action.id as string];
          draft.linkedin.selected = convLi;

          conversationsAdapter.updateOne(
            { ...convLi, unreadCount: 0 },
            draft.linkedin[draft.conversationStatus]
          );
        }
        return draft;
      case addLinkedinMessageAction.type:
        {
          let conv =
            draft.linkedin[draft.conversationStatus].entities[action.id];
          if (conv) {
            if (draft.linkedin.selected?.id === conv.id) {
              messagesAdapter.setOne(action.message, draft.linkedin.messages);
            } else {
              conv = { ...conv, unreadCount: conv.unreadCount + 1 };
            }
            conversationsAdapter.updateOne(
              {
                ...conv,
                snippet: action.message.message,
                messageType: action.message.type,
                timestamp: action.message.timestamp,
              },
              draft.linkedin[draft.conversationStatus]
            );
            conversationsAdapter.moveToFirstPosition(
              conv.id,
              draft.linkedin[draft.conversationStatus]
            );
          }
        }
        return draft;
      case postLinkedinMessageSuccessAction.type:
        {
          let conv =
            draft.linkedin[draft.conversationStatus].entities[action.id];
          if (conv) {
            if (draft.linkedin.selected?.id === conv.id) {
              messagesAdapter.setOne(action.message, draft.linkedin.messages);
            } else {
              conv = { ...conv, unreadCount: conv.unreadCount + 1 };
            }
            messagesAdapter.setOne(action.message, draft.linkedin.messages);
            conversationsAdapter.updateOne(
              {
                ...conv,
                snippet: action.message.message,
                messageType: action.message.type,
                timestamp: action.message.timestamp,
              },
              draft.linkedin[draft.conversationStatus]
            );
            conversationsAdapter.moveToFirstPosition(
              conv.id,
              draft.linkedin[draft.conversationStatus]
            );
          }
        }
        return draft;
      case getLinkedinConversationsSuccessAction.type:
        conversationsAdapter.addMany(action.active, draft.linkedin.active);
        return draft;
      case getLinkedinMessagesSuccessAction.type:
        messagesAdapter.addMany(action.messages, draft.linkedin.messages);
        return draft;
      case getLinkedinMessagesInitAction.type:
        messagesAdapter.removeAll(draft.linkedin.messages);
        return draft;

      case setSelectedLinkedinBusinessConversationAction.type:
        if (
          draft.linkedinBusiness.selected?.fromId === action.id ||
          !action.id
        ) {
          draft.linkedinBusiness.selected = null;
        } else {
          const convLb =
            draft.linkedinBusiness[draft.conversationStatus].entities[
              action.id as string
            ];
          draft.linkedinBusiness.selected = convLb;

          conversationsAdapter.updateOne(
            { ...convLb, unreadCount: 0 },
            draft.linkedinBusiness[draft.conversationStatus]
          );
        }
        return draft;
      case addLinkedinBusinessMessageAction.type:
        {
          let conv =
            draft.linkedinBusiness[draft.conversationStatus].entities[
              action.id
            ];
          if (conv) {
            if (draft.linkedinBusiness.selected?.id === conv.id) {
              messagesAdapter.setOne(
                action.message,
                draft.linkedinBusiness.messages
              );
            } else {
              conv = { ...conv, unreadCount: conv.unreadCount + 1 };
            }
            conversationsAdapter.updateOne(
              {
                ...conv,
                snippet: action.message.message,
                messageType: action.message.type,
                timestamp: action.message.timestamp,
              },
              draft.linkedinBusiness[draft.conversationStatus]
            );
            conversationsAdapter.moveToFirstPosition(
              conv.id,
              draft.linkedinBusiness[draft.conversationStatus]
            );
          }
        }
        return draft;
      case postLinkedinBusinessMessageSuccessAction.type:
        {
          let conv =
            draft.linkedinBusiness[draft.conversationStatus].entities[
              action.id
            ];
          if (conv) {
            if (draft.linkedinBusiness.selected?.id === conv.id) {
              messagesAdapter.setOne(
                action.message,
                draft.linkedinBusiness.messages
              );
            } else {
              conv = { ...conv, unreadCount: conv.unreadCount + 1 };
            }
            messagesAdapter.setOne(
              action.message,
              draft.linkedinBusiness.messages
            );
            conversationsAdapter.updateOne(
              {
                ...conv,
                snippet: action.message.message,
                messageType: action.message.type,
                timestamp: action.message.timestamp,
              },
              draft.linkedinBusiness[draft.conversationStatus]
            );
            conversationsAdapter.moveToFirstPosition(
              conv.id,
              draft.linkedinBusiness[draft.conversationStatus]
            );
          }
        }
        return draft;
      case getLinkedinBusinessConversationsSuccessAction.type:
        conversationsAdapter.addMany(
          action.active,
          draft.linkedinBusiness.active
        );
        return draft;
      case getLinkedinBusinessMessagesSuccessAction.type:
        messagesAdapter.addMany(
          action.messages,
          draft.linkedinBusiness.messages
        );
        return draft;
      case getLinkedinBusinessMessagesInitAction.type:
        messagesAdapter.removeAll(draft.linkedinBusiness.messages);
        return draft;

      case setSelectedWhatsappConversationAction.type:
        if (draft.whatsapp.selected?.id === action.id || !action.id) {
          draft.whatsapp.selected = null;
        } else {
          const conv9 =
            draft.whatsapp[draft.conversationStatus].entities[action.id];
          draft.whatsapp.selected = conv9;

          conversationsAdapter.updateOne(
            { ...conv9, unreadCount: 0 },
            draft.whatsapp[draft.conversationStatus]
          );
        }
        return draft;
      case addWhatsappMessageAction.type:
        let conv7 =
          draft.whatsapp[draft.conversationStatus].entities[action.id];

        if (conv7) {
          if (draft.whatsapp.selected?.id === conv7.id) {
            messagesAdapter.setOne(action.message, draft.whatsapp.messages);
          } else {
            conv7 = { ...conv7, unreadCount: conv7.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv7,
              snippet: action.message.message,
              messageType: action.message.type,
              timestamp: action.message.timestamp,
            },
            draft.whatsapp[draft.conversationStatus]
          );
          conversationsAdapter.moveToFirstPosition(
            conv7.id,
            draft.whatsapp[draft.conversationStatus]
          );
        }

        return draft;
      case addWhatsappConversationAction.type:
        conversationsAdapter.setOne(
          action.conversation,
          draft.whatsapp[draft.conversationStatus]
        );
        return draft;
      case postWhatsappMessageSuccessAction.type:
        let conv8 =
          draft.whatsapp[draft.conversationStatus].entities[action.id];

        if (conv8) {
          if (draft.whatsapp.selected?.id === conv8.id) {
            messagesAdapter.setOne(action.message, draft.whatsapp.messages);
          } else {
            conv8 = { ...conv8, unreadCount: conv8.unreadCount + 1 };
          }

          conversationsAdapter.updateOne(
            {
              ...conv8,
              snippet: action.message.message,
              messageType: action.message.type,
              timestamp: action.message.timestamp,
            },
            draft.whatsapp[draft.conversationStatus]
          );
          conversationsAdapter.moveToFirstPosition(
            conv8.id,
            draft.whatsapp[draft.conversationStatus]
          );
        }

        return draft;

      case getWhatsappConversationsSuccessAction.type:
        conversationsAdapter.addMany(action.inTrash, draft.whatsapp.inTrash);
        conversationsAdapter.addMany(action.archived, draft.whatsapp.archived);
        conversationsAdapter.addMany(action.active, draft.whatsapp.active);
        return draft;
      case getWhatsappMessagesSuccessAction.type:
        messagesAdapter.addMany(action.messages, draft.whatsapp.messages);
        return draft;
      case resetAction.type:
        return initialState;
      case selectConversationStatusToShow.type:
        draft.conversationStatus = action.conversationStatus;
        return draft;
      case syncMetaConversationWithContactAction.type:
        draft[action.social].selected!.participants[0] = action.contact;
        let conv9 =
          draft[action.social][draft.conversationStatus].entities[
            draft[action.social].selected!.id
          ];
        conv9.participants[0] = action.contact;
        conversationsAdapter.updateOne(
          {
            ...conv9,
          },
          draft[action.social][draft.conversationStatus]
        );
        return draft;
      case getInstagramConversationsInitAction.type:
      case getInstagramConversationsFailureAction.type:
      case getInstagramMessagesFailureAction.type:
      case postInstagramMessageFailureAction.type:
        removeOptimisticMessages(draft.instagram.messages);
        return draft;
      case postInstagramMessageInitAction.type:
        applyOptimisticOutgoingMessage(
          draft,
          "instagram",
          action.conversationId,
          action.message
        );
        return draft;
      case getFacebookConversationsInitAction.type:
      case getFacebookConversationsFailureAction.type:
      case getFacebookMessagesFailureAction.type:
      case postFacebookMessageFailureAction.type:
        removeOptimisticMessages(draft.facebook.messages);
        return draft;
      case postFacebookMessageInitAction.type:
        applyOptimisticOutgoingMessage(
          draft,
          "facebook",
          action.conversationId,
          action.message
        );
        return draft;
      case postFacebookSeenMessageInitAction.type:
      case postFacebookSeenMessageSuccessAction.type:
      case postFacebookSeenMessageFailureAction.type:
      case getLinkedinConversationsInitAction.type:
      case getLinkedinConversationsFailureAction.type:
      case getLinkedinMessagesFailureAction.type:
      case postLinkedinMessageInitAction.type:
      case postLinkedinMessageFailureAction.type:
      case getLinkedinBusinessConversationsInitAction.type:
      case getLinkedinBusinessConversationsFailureAction.type:
      case getLinkedinBusinessMessagesFailureAction.type:
      case postLinkedinBusinessMessageInitAction.type:
      case postLinkedinBusinessMessageFailureAction.type:
        return draft;
      default:
        return draft;
    }
  }
);

export default function socialChatReducer(
  state: SocialChatState = initialState,
  action: SocialChatAction | SocialChatApiAction | GlobalActions
) {
  const safe = mergePersistedSocialChat(state ?? initialState);
  return socialChatImmerReducer(safe, action);
}
